import React, { useEffect, useRef, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import { getAccessToken } from '../../../auth';
import { envConfig } from '../../../config';

const textDecoder = new TextDecoder();

interface ITerminalViewerProps {
	taskId: string | null;
	projectId: string;
	entityId: string;
	envIdentifier: string;
	tenantAccountId: string | null;
	stoppedReason?: string;
	taskStatus?: string;
}

const TerminalViewer: React.FC<ITerminalViewerProps> = ({
	taskId,
	projectId,
	entityId,
	envIdentifier,
	tenantAccountId,
	stoppedReason,
	taskStatus
}) => {
	const terminalRef = useRef<HTMLDivElement>(null);
	const xtermRef = useRef<Terminal | null>(null);
	const socketRef = useRef<WebSocket | null>(null);
	const ssmInitialized = useRef(false);
	const reconnectAttemptsRef = useRef(0);
	const reconnectTimerRef = useRef<number | null>(null);
	const manualCloseRef = useRef(false);
	const messageQueueRef = useRef<string[]>([]);

	const [isLoading, setIsLoading] = useState(false);
	const [isConnected, setIsConnected] = useState(false);
	const [hasEverConnected, setHasEverConnected] = useState(false);
	const [autoConnectEnabled, setAutoConnectEnabled] = useState(true);
	const autoConnectRef = useRef(true);
	const [terminalOverlayState, setTerminalOverlayState] = useState<{
		type: 'loading' | 'error';
		errorCode?: string;
		message: string;
	} | null>(null);

	const connectToTerminal = async (targetTaskId: string) => {
		if (taskStatus === 'STOPPED') {
			setTerminalOverlayState({
				type: 'error',
				errorCode: 'TASK_STOPPED',
				message: 'This container has been stopped.'
			});
			return;
		}

		if (
			!targetTaskId ||
			targetTaskId === 'provisioning' ||
			targetTaskId === 'Unknown'
		) {
			console.log('Task is provisioning, skipping connection attempt');
			return;
		}

		if (reconnectTimerRef.current) {
			window.clearTimeout(reconnectTimerRef.current);
			reconnectTimerRef.current = null;
		}

		if (socketRef.current) {
			manualCloseRef.current = true;
			socketRef.current.close();
			socketRef.current = null;
		}

		manualCloseRef.current = false;
		setIsLoading(true);
		setIsConnected(false);
		setTerminalOverlayState({
			type: 'loading',
			message: 'Connecting to compute instance...'
		});

		try {
			const accessToken = await getAccessToken();

			if (xtermRef.current) {
				const computeBaseUrl = envConfig.API_BASE_URL.replace(
					'//api.',
					'//compute.'
				);
				const wsUrl = new URL(
					`${computeBaseUrl}/compute-access-service/v1/sessions/stream`
				);
				wsUrl.protocol = wsUrl.protocol.replace('http', 'ws');
				wsUrl.searchParams.set('token', accessToken);
				wsUrl.searchParams.set('projectId', projectId);
				wsUrl.searchParams.set('entityId', entityId);
				wsUrl.searchParams.set('envIdentifier', envIdentifier);
				if (tenantAccountId)
					wsUrl.searchParams.set('awsAccountId', tenantAccountId);

				const socket = new WebSocket(wsUrl.toString());
				socketRef.current = socket;
				socket.binaryType = 'arraybuffer';

				socket.addEventListener('message', async (event) => {
					try {
						if (manualCloseRef.current) {
							return;
						}

						let textToWrite = '';
						if (typeof event.data === 'string') {
							// Check if the backend sent a JSON control/error message
							try {
								const parsed = JSON.parse(event.data);

								if (parsed.errorCode) {
									setTerminalOverlayState({
										type: 'error',
										errorCode: parsed.errorCode,
										message:
											parsed.message ||
											'An error occurred'
									});
									manualCloseRef.current = true;
									socket.close();
									return;
								} else if (parsed.error) {
									setTerminalOverlayState({
										type: 'error',
										message: parsed.error
									});
									manualCloseRef.current = true;
									socket.close();
									return;
								} else if (parsed.message) {
									setTerminalOverlayState({
										type: 'loading',
										message: parsed.message
									});
									return;
								}
							} catch (e) {
								// Not JSON, just standard string text
							}

							textToWrite = event.data;
						} else if (event.data instanceof ArrayBuffer) {
							textToWrite = textDecoder.decode(
								new Uint8Array(event.data)
							);
						} else if (event.data instanceof Blob) {
							const arrayBuffer = await event.data.arrayBuffer();
							textToWrite = textDecoder.decode(
								new Uint8Array(arrayBuffer)
							);
						}

						if (textToWrite) {
							xtermRef.current?.write(textToWrite);
						}
					} catch (error) {
						console.error('Error handling message:', error);
					}
				});

				socket.addEventListener('open', () => {
					ssmInitialized.current = true;
					setIsConnected(true);
					setHasEverConnected(true);
					setTerminalOverlayState(null);
					reconnectAttemptsRef.current = 0;
					xtermRef.current?.writeln(
						'\r\n\x1b[32mConnected to ' +
							targetTaskId +
							'\x1b[0m\r\n'
					);

					// flush queue
					const toSend = messageQueueRef.current.splice(
						0,
						messageQueueRef.current.length
					);
					for (const msg of toSend) {
						socket.send(msg);
					}
				});

				socket.addEventListener('close', () => {
					setIsConnected(false);
					ssmInitialized.current = false;
					xtermRef.current?.writeln(
						'\r\n\x1b[31mConnection closed.\x1b[0m'
					);

					if (!manualCloseRef.current && autoConnectRef.current) {
						const base = Math.min(
							500 * 2 ** reconnectAttemptsRef.current,
							15000
						);
						const delay = base + Math.floor(Math.random() * 800);
						reconnectAttemptsRef.current++;

						setTerminalOverlayState((prev) => {
							if (prev?.type === 'error') return prev;
							return {
								type: 'loading',
								message: `Reconnecting in ${Math.round(delay / 1000)}s...`
							};
						});

						reconnectTimerRef.current = window.setTimeout(() => {
							connectToTerminal(targetTaskId);
						}, delay);
					}
				});

				socket.addEventListener('error', (error) => {
					console.error('WebSocket error:', error);
				});

				xtermRef.current.onData((data) => {
					if (socket.readyState === WebSocket.OPEN) {
						socket.send(data);
					} else {
						messageQueueRef.current.push(data);
					}
				});
			}
		} catch (error) {
			console.error('Failed to get access token:', error);
			setTerminalOverlayState({
				type: 'error',
				message: 'Authentication failed'
			});
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (terminalRef.current && !xtermRef.current) {
			const term = new Terminal({
				cursorBlink: true,
				convertEol: true,
				fontSize: 11,
				fontFamily: 'Menlo, Monaco, "Courier New", monospace',
				theme: {
					background: '#0d1117',
					foreground: '#e6edf3',
					cursor: '#58a6ff',
					black: '#484f58',
					red: '#ff7b72',
					green: '#3fb950',
					yellow: '#d29922',
					blue: '#58a6ff',
					magenta: '#bc8cff',
					cyan: '#39c5cf',
					white: '#b1bac4'
				}
			});
			const fitAddon = new FitAddon();
			term.loadAddon(fitAddon);
			term.open(terminalRef.current);
			
			// Initial fit
			setTimeout(() => {
				try {
					fitAddon.fit();
				} catch (e) {}
			}, 100);
			
			xtermRef.current = term;

			const resizeObserver = new ResizeObserver(() => {
				try {
					fitAddon.fit();
				} catch (e) {}
			});
			resizeObserver.observe(terminalRef.current);

			return () => {
				resizeObserver.disconnect();
				term.dispose();
			};
		}
	}, []);

	useEffect(() => {
		if (taskId && xtermRef.current) {
			connectToTerminal(taskId);
		}

		return () => {
			if (socketRef.current) {
				manualCloseRef.current = true;
				socketRef.current.close();
				socketRef.current = null;
			}
			if (reconnectTimerRef.current) {
				window.clearTimeout(reconnectTimerRef.current);
				reconnectTimerRef.current = null;
			}
		};
	}, [taskId, entityId, projectId, envIdentifier, tenantAccountId]);

	return (
		<div
			style={{
				flex: 1,
				display: 'flex',
				flexDirection: 'column',
				border: '1px solid #2d2d2d',
				borderRadius: '10px',
				backgroundColor: '#0d1117',
				boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
				overflow: 'hidden'
			}}
		>
			{/* Terminal Header */}
			<div
				style={{
					backgroundColor: '#161b22',
					padding: '8px 16px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					borderBottom: '1px solid #21262d'
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '6px'
					}}
				>
					<div
						style={{
							width: '12px',
							height: '12px',
							borderRadius: '50%',
							backgroundColor: '#ff5f56'
						}}
					/>
					<div
						style={{
							width: '12px',
							height: '12px',
							borderRadius: '50%',
							backgroundColor: '#ffbd2e'
						}}
					/>
					<div
						style={{
							width: '12px',
							height: '12px',
							borderRadius: '50%',
							backgroundColor: '#27c93f'
						}}
					/>
					<span
						style={{
							marginLeft: '12px',
							color: '#8b949e',
							fontFamily: 'monospace',
							fontSize: '11px'
						}}
					>
						{taskId || 'Select an instance'}
					</span>
				</div>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					}}
				>
					{hasEverConnected && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '6px',
								marginRight: '8px'
							}}
						>
							<span
								style={{
									fontSize: '11px',
									color: '#8b949e',
									fontFamily: 'monospace'
								}}
							>
								Auto-connect
							</span>
							<div
								onClick={() => {
									const newVal = !autoConnectEnabled;
									setAutoConnectEnabled(newVal);
									autoConnectRef.current = newVal;
								}}
								style={{
									width: '24px',
									height: '14px',
									backgroundColor: autoConnectEnabled ? '#238636' : '#21262d',
									borderRadius: '12px',
									position: 'relative',
									cursor: 'pointer',
									transition: 'background-color 0.2s',
									border: '1px solid #30363d'
								}}
							>
								<div
									style={{
										width: '10px',
										height: '10px',
										backgroundColor: '#ffffff',
										borderRadius: '50%',
										position: 'absolute',
										top: '1px',
										left: autoConnectEnabled ? '11px' : '1px',
										transition: 'left 0.2s',
										boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
									}}
								/>
							</div>
						</div>
					)}
					{!isConnected && taskId && !isLoading && (
						<button
							onClick={() => connectToTerminal(taskId)}
							style={{
								background: '#238636',
								color: '#ffffff',
								border: 'none',
								padding: '2px 8px',
								borderRadius: '4px',
								fontSize: '11px',
								cursor: 'pointer',
								fontFamily: 'monospace',
								marginRight: '8px'
							}}
						>
							Reconnect
						</button>
					)}
					<div
						style={{
							width: '8px',
							height: '8px',
							borderRadius: '50%',
							backgroundColor: isConnected ? '#3fb950' : '#8b949e'
						}}
					/>
					<span
						style={{
							fontSize: '11px',
							color: '#8b949e',
							fontFamily: 'monospace'
						}}
					>
						{isConnected ? 'Connected' : 'Disconnected'}
					</span>
				</div>
			</div>

			{/* Terminal Body */}
			{terminalOverlayState?.type === 'loading' && (
				<div
					style={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '32px',
						backgroundColor: '#0d1117',
						color: '#e6edf3',
						textAlign: 'center',
						height: '100%'
					}}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: '16px'
						}}
					>
						{/* Loading Spinner */}
						<div
							style={{
								width: '24px',
								height: '24px',
								border: '3px solid rgba(255, 255, 255, 0.1)',
								borderTop: '3px solid #6366f1',
								borderRadius: '50%',
								animation: 'spin 1s linear infinite'
							}}
						/>
						<style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
						<span
							style={{
								fontSize: '14px',
								color: '#8b949e',
								fontFamily: 'monospace'
							}}
						>
							{terminalOverlayState.message}
						</span>
					</div>
				</div>
			)}
			{terminalOverlayState?.type === 'error' && (
				<div
					style={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '32px',
						backgroundColor: '#0d1117',
						color: '#e6edf3',
						textAlign: 'center',
						height: '100%'
					}}
				>
					<div
						style={{
							backgroundColor: 'rgba(248, 81, 73, 0.1)',
							border: '1px solid rgba(248, 81, 73, 0.4)',
							borderRadius: '8px',
							padding: '32px',
							maxWidth: '450px'
						}}
					>
						<div style={{ fontSize: '32px', marginBottom: '16px' }}>
							⚠️
						</div>
						<h3
							style={{
								margin: '0 0 12px 0',
								fontSize: '18px',
								fontWeight: 600,
								color: '#f85149'
							}}
						>
							{terminalOverlayState.errorCode ===
							'SSM_AGENT_TIMEOUT'
								? 'Terminal Access Unavailable'
								: terminalOverlayState.errorCode === 'TASK_STOPPED'
									? 'Container Stopped'
									: 'Connection Error'}
						</h3>
						<p
							style={{
								margin: '0 0 16px 0',
								fontSize: '14px',
								lineHeight: '1.5',
								color: '#8b949e'
							}}
						>
							{terminalOverlayState.errorCode ===
							'SSM_AGENT_TIMEOUT' ? (
								<>
									This container either lacks a shell (
									<code
										style={{
											background: 'rgba(110,118,129,0.4)',
											padding: '2px 6px',
											borderRadius: '4px',
											color: '#e6edf3'
										}}
									>
										/bin/sh
									</code>
									) or its main process has already finished
									executing.
								</>
							) : (
								terminalOverlayState.message
							)}
						</p>
						{stoppedReason && (
							<div
								style={{
									marginTop: '16px',
									padding: '12px',
									backgroundColor: 'rgba(13, 17, 23, 0.5)',
									borderRadius: '6px',
									border: '1px solid rgba(139, 148, 158, 0.2)',
									textAlign: 'left'
								}}
							>
								<div style={{ fontSize: '12px', fontWeight: 600, color: '#8b949e', marginBottom: '6px', textTransform: 'uppercase' }}>
									Task Stopped Reason
								</div>
								<div style={{ fontSize: '13px', color: '#e6edf3', fontFamily: 'monospace', wordBreak: 'break-word' }}>
									{stoppedReason}
								</div>
							</div>
						)}
						{terminalOverlayState.errorCode ===
							'SSM_AGENT_TIMEOUT' && !stoppedReason && (
							<p
								style={{
									margin: 0,
									fontSize: '13px',
									color: '#8b949e',
									lineHeight: '1.5'
								}}
							>
								Terminal access requires a running shell to
								connect. If this is a custom image, ensure it is
								designed to run continuously and includes
								standard shell utilities.
							</p>
						)}
					</div>
				</div>
			)}
			<div
				style={{
					flex: 1,
					padding: '16px',
					overflow: 'hidden',
					backgroundColor: '#0d1117',
					display: terminalOverlayState ? 'none' : 'flex',
					flexDirection: 'column'
				}}
			>
				<div
					ref={terminalRef}
					style={{
						flex: 1,
						overflow: 'hidden',
						letterSpacing: 'normal'
					}}
				/>
			</div>
		</div>
	);
};

export default TerminalViewer;
